export const CLINIC_CRF_V1 = 'clinic-crf.v1' as const
export const CLINIC_CRF_V2 = 'clinic-crf.v2' as const
export const CLINIC_CRF_PACKAGE_V1 = 'clinic-crf-package.v1' as const

export const FIELD_TYPES = [
  'string',
  'text',
  'integer',
  'number',
  'boolean',
  'date',
  'datetime',
  'choice',
  'multichoice',
  'partial_date',
] as const

export type ClinicCrfFieldType = (typeof FIELD_TYPES)[number]

export const FIELD_SOURCES = ['site', 'subject', 'device', 'ehr', 'derived'] as const
export type ClinicCrfFieldSource = (typeof FIELD_SOURCES)[number]

export const MISSING_CODES = [
  'unknown',
  'not_done',
  'not_applicable',
  'withheld',
  'other',
] as const
export type ClinicCrfMissingCode = (typeof MISSING_CODES)[number]

export const CONDITION_OPERATORS = [
  'eq',
  'neq',
  'gt',
  'gte',
  'lt',
  'lte',
  'in',
  'not_in',
  'present',
  'missing',
] as const
export type ClinicCrfConditionOperator = (typeof CONDITION_OPERATORS)[number]

export type JsonScalar = string | number | boolean | null

export interface ClinicCrfConditionLeaf {
  field: string
  operator: ClinicCrfConditionOperator
  value?: JsonScalar | JsonScalar[]
  compare_field?: string
}

export type ClinicCrfCondition =
  | ClinicCrfConditionLeaf
  | { all: ClinicCrfCondition[] }
  | { any: ClinicCrfCondition[] }
  | { not: ClinicCrfCondition }

export interface ClinicCrfField {
  path: string
  label: string
  type: ClinicCrfFieldType
  required?: boolean
  unit?: string | null
  min?: number
  max?: number
  precision?: number
  options?: string[]
  terminology?: Record<string, unknown> | unknown[]
  source?: ClinicCrfFieldSource
  min_length?: number
  max_length?: number
  missing_codes?: ClinicCrfMissingCode[]
  missing_reason_required?: boolean
  visible_when?: ClinicCrfCondition
  required_when?: ClinicCrfCondition
}

export interface ClinicCrfRule {
  id: string
  path: string
  message: string
  assert: ClinicCrfCondition
  when?: ClinicCrfCondition
}

export interface ClinicCrfDefinitionV1 {
  version: typeof CLINIC_CRF_V1
  fields: Omit<
    ClinicCrfField,
    'missing_codes' | 'missing_reason_required' | 'visible_when' | 'required_when'
  >[]
}

export interface ClinicCrfDefinitionV2 {
  version: typeof CLINIC_CRF_V2
  fields: ClinicCrfField[]
  rules?: ClinicCrfRule[]
}

export type ClinicCrfDefinition = ClinicCrfDefinitionV1 | ClinicCrfDefinitionV2

export interface ClinicCrfPackageForm {
  code: string
  name: string
  version: number
  description?: string
}

export interface ClinicCrfPresentationSection {
  id: string
  title: string
  description?: string
  field_paths: string[]
}

export interface ClinicCrfFieldPresentation {
  help_text?: string
  placeholder?: string
  option_labels?: Record<string, string>
  width?: 'full' | 'half'
}

export interface ClinicCrfPresentation {
  default_locale: string
  sections: ClinicCrfPresentationSection[]
  fields?: Record<string, ClinicCrfFieldPresentation>
}

export interface ClinicCrfPackageProvenance {
  generator: string
  generator_version: string
  exported_at: string
}

export interface ClinicCrfPackageIntegrity {
  definition_sha256?: string
}

export interface ClinicCrfPackageV1 {
  format: typeof CLINIC_CRF_PACKAGE_V1
  form: ClinicCrfPackageForm
  definition: ClinicCrfDefinitionV2
  presentation?: ClinicCrfPresentation
  provenance: ClinicCrfPackageProvenance
  integrity?: ClinicCrfPackageIntegrity
}

export interface ValidationIssue {
  path: string
  message: string
  severity: 'error' | 'warning'
  code: string
}

export interface ValidationResult<T> {
  value: T | null
  issues: ValidationIssue[]
  migratedFrom?: typeof CLINIC_CRF_V1
}

export interface MissingValue {
  __missing: {
    code: ClinicCrfMissingCode
    reason?: string
  }
}

export type PartialDateValue = { year: number; month?: number; day?: number }
export type ClinicCrfAnswer =
  | string
  | number
  | boolean
  | string[]
  | MissingValue
  | PartialDateValue
  | null

export type ClinicCrfData = Record<string, unknown>

export interface FieldEvaluation {
  field: ClinicCrfField
  visible: boolean
  required: boolean
  errors: string[]
}

export interface RuleEvaluation {
  rule: ClinicCrfRule
  active: boolean
  passed: boolean
}

export interface DataValidationResult {
  errors: Record<string, string[]>
  fields: FieldEvaluation[]
  rules: RuleEvaluation[]
  isValid: boolean
}
